import React, { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";

// ─────────────────────────────────────────────────────────────────────────────
// 1. SINGLE WAREHOUSE REQUEST PRINT (For physical stock transfer)
// ─────────────────────────────────────────────────────────────────────────────
export const WarehouseRequestPrint = React.forwardRef(({ data }, ref) => {
  const barcodeRef = useRef(null);
  const reqNumber = data?.requestNumber;

  useEffect(() => {
    if (reqNumber && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, reqNumber, {
          format: "CODE128",
          lineColor: "#000000",
          width: 1.5,
          height: 40,
          displayValue: false,
          margin: 0,
        });
      } catch (e) {
        console.error("Barcode error", e);
      }
    }
  }, [reqNumber]);

  if (!data) return null;

  const rawItems = data.items || [];
  const status = (data.status || "").toLowerCase();

  // Dynamic document title based on approval status
  const documentTitle =
    status === "approved"
      ? "APPROVED TRANSFER SLIP"
      : "WAREHOUSE TRANSFER REQUEST";

  const requestDate = data.requestDate || data.createdDate;

  // Calculate totals
  const totalRequested = rawItems.reduce(
    (s, i) => s + (i.requestedQuantity || 0),
    0,
  );
  const totalApproved = rawItems.reduce(
    (s, i) => s + (i.approvedQuantity || 0),
    0,
  );

  return (
    <div
      ref={ref}
      className="p-8 bg-white text-black text-xs font-sans max-w-[800px] mx-auto"
    >
      {/* ── HEADER ── */}
      <div className="text-center mb-6">
        <h1 className="text-xl font-bold uppercase">Ichthus Technology</h1>
        <div className="text-[10px] text-gray-600">
          <p># 3128 Sto. Rosario Street, Barangay</p>
          <p>Mapulang Lupa, Valenzuela, 1440 Metro</p>
        </div>
        <div className="mt-2 flex justify-center">
          <svg ref={barcodeRef}></svg>
        </div>
        <p className="text-[9px] mt-1 tracking-widest">{reqNumber}</p>
      </div>

      {/* ── INFO GRID ── */}
      <div className="flex justify-between items-start mb-6 gap-4">
        {/* Transfer Details */}
        <div className="w-1/2">
          <h2 className="font-bold text-sm uppercase mb-1 bg-gray-100 p-1">
            Transfer Details:
          </h2>
          <div className="pl-1 space-y-1">
            <p>
              <span className="font-bold text-gray-600 uppercase">
                Destination Warehouse:
              </span>
              <br />
              <span className="font-bold text-sm uppercase text-gray-900">
                {data.warehouseName || "N/A"}
              </span>
            </p>
            <p>
              <span className="font-bold text-gray-600 uppercase">
                Requested By:
              </span>
              <br />
              <span className="uppercase text-gray-800">
                {data.requestedByFullName || "N/A"}
              </span>
            </p>
            {data.notes && (
              <p>
                <span className="font-bold text-gray-600 uppercase">
                  Notes / Purpose:
                </span>
                <br />
                <span className="italic text-gray-700">{data.notes}</span>
              </p>
            )}
          </div>
        </div>

        {/* Document Details */}
        <div className="w-1/2">
          <h2 className="font-bold text-lg mb-2 text-right">{documentTitle}</h2>
          <div className="grid grid-cols-2 gap-y-1 text-xs text-right">
            <span className="font-bold text-gray-600">Request No.</span>
            <span className="font-semibold">{reqNumber}</span>

            <span className="font-bold text-gray-600">Date Requested</span>
            <span>
              {requestDate ? new Date(requestDate).toLocaleDateString() : "—"}
            </span>

            <span className="font-bold text-gray-600">Status</span>
            <span className="uppercase font-bold">{data.status}</span>
          </div>

          <div className="mt-4 bg-gray-100 p-2 border border-gray-200">
            <div className="flex justify-between font-bold mb-1">
              <span className="text-gray-600">Total Requested:</span>
              <span>{totalRequested} units</span>
            </div>
            <div className="flex justify-between font-bold text-indigo-700">
              <span>Total Approved:</span>
              <span>{totalApproved} units</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── TABLE HEADER ── */}
      <div className="mt-4 border-b-2 border-black pb-1 mb-1 bg-gray-100 flex font-bold text-xs uppercase p-1">
        <div className="w-8 text-center">#</div>
        <div className="w-28 pl-2">Item Code</div>
        <div className="flex-1">Description</div>
        <div className="w-24 text-center">Req. Qty</div>
        <div className="w-24 text-center">Apprv. Qty</div>
        <div className="w-28 text-center">Actual Transfer</div>
      </div>

      {/* ── TABLE BODY ── */}
      <div className="min-h-[300px] font-sans tracking-tight">
        <div className="divide-y divide-gray-100 border-b border-gray-100">
          {rawItems.map((item, index) => {
            const reqQty = item.requestedQuantity || 0;
            const appQty = item.approvedQuantity ?? "—";
            const itemCode = item.itemCode || "—";

            return (
              <div
                key={item.id || index}
                className="flex items-center py-2 text-[10px] uppercase font-bold leading-snug"
              >
                <div className="w-8 text-center text-gray-400">
                  {(index + 1).toString().padStart(2, "0")}
                </div>

                <div className="w-28 text-indigo-600 truncate px-2 font-mono">
                  {itemCode}
                </div>

                <div className="flex-1 text-gray-800 pr-2">
                  {item.productName}
                </div>

                <div className="w-24 text-center text-gray-600 tabular-nums">
                  {reqQty}
                </div>

                <div className="w-24 text-center text-green-700 tabular-nums font-black">
                  {appQty}
                </div>

                {/* Blank line for warehouse staff to write the physically transferred amount */}
                <div className="w-28 px-4">
                  <div className="border-b border-gray-400 h-4 w-full"></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── SIGNATURES ── */}
      <div className="mt-16 grid grid-cols-4 gap-6 text-[10px]">
        {[
          "Requested By:",
          "Approved By:",
          "Released By (Origin):",
          "Received By (Dest):",
        ].map((label) => (
          <div key={label}>
            <div className="border-b border-black mb-1 h-6"></div>
            <p className="font-bold text-gray-600">{label}</p>
            <p className="text-[8px] text-gray-400 mt-0.5">
              Signature over printed name
            </p>
          </div>
        ))}
      </div>

      <div className="mt-8 text-center text-[9px] text-gray-400">
        System Generated {documentTitle} ({reqNumber}) | Printed on{" "}
        {new Date().toLocaleString()}
      </div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// 2. GENERAL REPORT PRINT (For printing a filtered list/summary)
// ─────────────────────────────────────────────────────────────────────────────
export const WarehouseRequestListPrint = React.forwardRef(
  ({ dataList, filters }, ref) => {
    if (!dataList || dataList.length === 0) return null;

    return (
      <div
        ref={ref}
        className="p-8 bg-white text-black text-xs font-sans max-w-[1000px] mx-auto landscape"
      >
        {/* ── HEADER ── */}
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold uppercase">Ichthus Technology</h1>
          <h2 className="text-sm font-bold mt-1 uppercase text-gray-700">
            Warehouse Requests Summary Report
          </h2>
          {filters && (
            <p className="text-[10px] text-gray-500 mt-1">
              Filter applied: {filters}
            </p>
          )}
        </div>

        {/* ── TABLE ── */}
        <table className="w-full text-left text-[10px] border-collapse">
          <thead>
            <tr className="bg-gray-100 border-y-2 border-black text-gray-800 uppercase tracking-wide">
              <th className="py-2 px-2">Request #</th>
              <th className="py-2 px-2">Date</th>
              <th className="py-2 px-2">Destination Warehouse</th>
              <th className="py-2 px-2">Requested By</th>
              <th className="py-2 px-2 text-center">Items</th>
              <th className="py-2 px-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {dataList.map((item, index) => {
              const itemsCount = item.items ? item.items.length : 0;
              return (
                <tr key={item.id || index} className="hover:bg-gray-50">
                  <td className="py-2 px-2 font-bold font-mono text-indigo-600">
                    {item.requestNumber}
                  </td>
                  <td className="py-2 px-2 text-gray-600">
                    {item.requestDate
                      ? new Date(item.requestDate).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="py-2 px-2 font-bold text-gray-800">
                    {item.warehouseName}
                  </td>
                  <td className="py-2 px-2 text-gray-700">
                    {item.requestedByFullName || "—"}
                  </td>
                  <td className="py-2 px-2 text-center font-semibold">
                    {itemsCount}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <span className="uppercase font-bold tracking-wider text-[9px]">
                      {item.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="mt-8 text-center text-[9px] text-gray-400 border-t border-gray-200 pt-2">
          Total Records: {dataList.length} | Printed on{" "}
          {new Date().toLocaleString()}
        </div>
      </div>
    );
  },
);
